
import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useIsMobile } from '@/hooks/use-mobile';

export interface PieChartComponentProps {
  data: any[];
  dataKey: string;
  nameKey: string;
  colors: string[];
  formatter?: (value: any, name?: string) => [string, string];
}

const PieChartComponent = ({
  data,
  dataKey,
  nameKey,
  colors,
  formatter,
}: PieChartComponentProps) => {
  const isMobile = useIsMobile();

  // Responsive settings
  const outerRadius = isMobile ? 70 : 120;
  const legendProps = {
    verticalAlign: isMobile ? 'bottom' : 'middle',
    align: isMobile ? 'center' : 'right',
    layout: isMobile ? 'horizontal' : 'vertical',
    wrapperStyle: {
      fontSize: isMobile ? '12px' : '14px',
      paddingTop: isMobile ? '10px' : '0px'
    }
  } as const;

  // Custom label function for better mobile display
  const renderLabel = isMobile ? false : ({ name, percent }) => {
    if (percent < 0.05) return ''; // Hide labels for very small slices
    return `${name}: ${(percent * 100).toFixed(0)}%`;
  };

  // Custom tooltip formatter
  const customTooltipFormatter = (value: any, name: string) => {
    if (formatter) {
      return formatter(value, name);
    }
    return [value, name];
  };

  // Filter out zero values to avoid empty slices
  const filteredData = data.filter(item => item[dataKey] > 0);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <Pie
          data={filteredData}
          cx="50%"
          cy={isMobile ? "40%" : "50%"}
          labelLine={false}
          label={renderLabel}
          outerRadius={outerRadius}
          fill="#8884d8"
          dataKey={dataKey}
          nameKey={nameKey}
        >
          {filteredData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <Tooltip 
          formatter={customTooltipFormatter}
          wrapperStyle={{ 
            fontSize: isMobile ? '12px' : '14px',
            maxWidth: isMobile ? '200px' : 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px'
          }}
          contentStyle={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
        <Legend 
          {...legendProps}
          iconSize={isMobile ? 8 : 10}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default PieChartComponent;
